import _ from 'underscore';
import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';

import { withDefaultStyles } from '@bingads-webui-react/with-default-styles';
import { Shimmer } from '@fluentui/react';

import {
  tileFooterConfigShape as footerConfigProp,
  tileHeaderConfigShape as headerConfigProp,
  tileContentConfigShape as contentConfigPro,
} from './shared-props';
import { getStyles, tileStyles } from './get-styles';
import { TileHeader } from './tile-header';
import { TileFooter } from './tile-footer';

export const SharedTileUnstyled = (props) => {
  const {
    config: {
      header: headerConfig,
      footer: footerConfig,
      content: contentConfig,
      tileClassName,
      tileId,
      tileRef,
      wide = false,
    },
    isLoading,
    classes,
    children,
    classPrefix = '',
  } = props;

  const { isLargeHeader = false } = headerConfig || {};

  const {
    onRenderShimmer,
    styles: {
      root = {},
    } = {},
    shimmerLineCount = 3,
    isShimmerRequired = false,
  } = contentConfig || {};

  const isShowingShimmer = useMemo(() =>
    isLoading && isShimmerRequired, [isLoading, isShimmerRequired]);

  const loadingItems = useMemo(() => {
    if (_.isFunction(onRenderShimmer)) {
      return onRenderShimmer();
    }
    const items = [];
    const shimmerWidthList = ['292px', '254px', '219px'];
    _.map(_.range(shimmerLineCount), (index) => {
      items.push(<Shimmer
        key={index}
        width={shimmerWidthList[index % _.size(shimmerWidthList)]}
        styles={tileStyles.shimmerItem}
      />);
    });
    return (
      <div className={classNames(`${classPrefix}tile-shimmer`, classes.contentContainer, classes.tileSection, 'tile-shimmer')}>
        {items}
      </div>
    );
  }, [classPrefix, classes.contentContainer, classes.tileSection, onRenderShimmer, shimmerLineCount]);

  return (
    <div id={tileId} className={classNames(`${classPrefix}tile-container`, tileClassName, 'tile-container')}>
      <div
        className={classNames(classes.tileContainer, wide ? classes.wideTileContainer : '', `${classPrefix}tile-content`, 'tile-content')}
        style={root}
        ref={tileRef}
      >
        {
          headerConfig &&
          <span className={classNames(classes.tileSection, isLargeHeader ? '' : classes.headerBorder, `${classPrefix}tile-header`, 'tile-header')}>
            <TileHeader config={headerConfig} classPrefix={classPrefix} />
          </span>
        }
        {
          isShowingShimmer && loadingItems
        }
        {
            !isShowingShimmer &&
            <div className={classNames(`${classPrefix}tile-contents-container`, classes.contentContainer, classes.tileSection, 'tile-contents-container')}>
              {children}
            </div>
        }
        {
          footerConfig && !isShowingShimmer &&
          <span className={classNames(classes.tileSection, `${classPrefix}tile-footer`, 'tile-footer')}>
            <TileFooter config={footerConfig} classPrefix={classPrefix} />
          </span>
        }
      </div>
    </div>
  );
};

SharedTileUnstyled.propTypes = {
  children: PropTypes.oneOfType([
    PropTypes.arrayOf(PropTypes.node),
    PropTypes.node,
  ]),
  classes: PropTypes.shape({
    headerContainer: PropTypes.string,
    tileContainer: PropTypes.string,
    footerContainer: PropTypes.string,
    dottedContainer: PropTypes.string,
    contentContainer: PropTypes.string,
  }).isRequired,
  config: PropTypes.shape({
    tileClassName: PropTypes.string.isRequired,
    tileId: PropTypes.string,
    tileRef: PropTypes.shape({}),
    wide: PropTypes.bool,
    header: headerConfigProp,
    footer: footerConfigProp,
    content: contentConfigPro,
  }),
  isLoading: PropTypes.bool,
};

SharedTileUnstyled.defaultProps = {
  config: {},
  children: [],
  isLoading: false,
};

export const SharedTile = withDefaultStyles(SharedTileUnstyled, getStyles);
